import { useCallback, useEffect, useState } from "react";

import PurchaseBillService from "../api/purchaseBill.service";

import {
  Pagination,
  PurchaseBill,
  PurchaseBillFilters,
  PurchaseBillPayload,
  PurchaseBillStatus,
} from "../types";

const DEFAULT_FILTERS: PurchaseBillFilters = {
  search: "",
  page: 1,
  limit: 10,
};

export const usePurchaseBills = () => {
  const [purchaseBills, setPurchaseBills] = useState<
    PurchaseBill[]
  >([]);

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState("");

  const [pagination, setPagination] =
    useState<Pagination>({
      total: 0,
      page: 1,
      limit: 10,
      totalPages: 0,
    });

  const [filters, setFilters] =
    useState<PurchaseBillFilters>(
      DEFAULT_FILTERS
    );

  const fetchPurchaseBills =
    useCallback(async () => {
      try {
        setLoading(true);
        setError("");

        const response =
          await PurchaseBillService.getPurchaseBills(
            filters
          );

        setPurchaseBills(response.items);
        setPagination(
          response.pagination
        );
      } catch (err: any) {
        setError(
          err?.response?.data?.message ??
            "Failed to load purchase bills."
        );
      } finally {
        setLoading(false);
      }
    }, [filters]);

  useEffect(() => {
    fetchPurchaseBills();
  }, [fetchPurchaseBills]);

  const createPurchaseBill =
    async (
      payload: PurchaseBillPayload
    ) => {
      await PurchaseBillService.createPurchaseBill(
        payload
      );

      await fetchPurchaseBills();
    };

  const updatePurchaseBill =
    async (
      id: string,
      payload: PurchaseBillPayload
    ) => {
      await PurchaseBillService.updatePurchaseBill(
        id,
        payload
      );

      await fetchPurchaseBills();
    };

  const deletePurchaseBill =
    async (id: string) => {
      await PurchaseBillService.deletePurchaseBill(
        id
      );

      await fetchPurchaseBills();
    };

  const updateStatus =
    async (
      id: string,
      status: PurchaseBillStatus,
      warehouseId?: string | null
    ) => {
      await PurchaseBillService.updateStatus(
        id,
        status,
        warehouseId
      );

      await fetchPurchaseBills();
    };

  const updateFilters = (
    values: Partial<PurchaseBillFilters>
  ) => {
    setFilters((prev) => ({
      ...prev,
      ...values,
    }));
  };

  return {
    purchaseBills,

    loading,

    error,

    pagination,

    filters,

    fetchPurchaseBills,

    createPurchaseBill,

    updatePurchaseBill,

    deletePurchaseBill,

    updateStatus,

    updateFilters,
  };
};
